/**
 * Pure TEA update for AccountPanel sign-in / license chrome.
 *
 * Shell: `AccountPanel.vue` (cloudAuth + desktopLicense Cmds).
 * Session / entitlement / license shapes are local so this module stays
 * free of `lib/cloud*` imports (ESLint pure-update rule).
 */
import { assertNever } from "../lib/assertNever";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type AccountPanelSession = {
  readonly email: string;
  readonly userId: string;
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly accessTokenExpiresIn: number;
  readonly savedAt: number;
};

export type AccountPanelEntitlement = {
  readonly kind: string;
  readonly status: string;
  readonly active: boolean;
};

export type AccountPanelLicense = {
  readonly email: string;
  readonly product: string;
  readonly licenseId: string;
  readonly issuedAt: string;
};

export type AccountStatusTone = "idle" | "waiting" | "success" | "error";

export type AccountPanelModel = {
  readonly session: Maybe<AccountPanelSession>;
  readonly entitlements: readonly AccountPanelEntitlement[];
  readonly localLicense: Maybe<AccountPanelLicense>;
  readonly email: string;
  readonly status: Maybe<string>;
  readonly statusTone: AccountStatusTone;
  readonly busy: boolean;
};

export function initialAccountPanelModel(
  session: Maybe<AccountPanelSession>,
): AccountPanelModel {
  return {
    session,
    entitlements: [],
    localLicense: nothing,
    email: matchMaybe(session, {
      just: (s) => s.email,
      nothing: () => "",
    }),
    status: nothing,
    statusTone: "idle",
    busy: false,
  };
}

export type AccountPanelMsg =
  | { readonly type: "Mounted" }
  | { readonly type: "EmailChanged"; readonly email: string }
  | { readonly type: "SendLink" }
  | { readonly type: "ActivateFromFile" }
  | { readonly type: "SignOut" }
  | { readonly type: "Close" }
  | { readonly type: "CloudAuthEvent" }
  | { readonly type: "Visibility" }
  | {
      readonly type: "EntitlementsLoaded";
      readonly entitlements: readonly AccountPanelEntitlement[];
    }
  | { readonly type: "EntitlementsFailed"; readonly error: string }
  | {
      readonly type: "LicenseLoaded";
      readonly license: Maybe<AccountPanelLicense>;
    }
  | {
      readonly type: "MagicLinkSucceeded";
      readonly session: AccountPanelSession;
    }
  | { readonly type: "MagicLinkFailed"; readonly error: string }
  | {
      readonly type: "ActivateSucceeded";
      readonly license: AccountPanelLicense;
    }
  | { readonly type: "ActivateFailed"; readonly error: string }
  | { readonly type: "SignedOut"; readonly doneMessage: string }
  | {
      readonly type: "SessionSynced";
      readonly session: AccountPanelSession;
    };

export type AccountPanelCmd =
  | { readonly kind: "refreshEntitlements" }
  | { readonly kind: "refreshLocalLicense" }
  | { readonly kind: "requestMagicLink"; readonly email: string }
  | { readonly kind: "activateLicenseFile" }
  | { readonly kind: "clearSessionAndLicense"; readonly doneMessage: string }
  | { readonly kind: "emitAuthChanged" }
  | { readonly kind: "emitClose" }
  | { readonly kind: "focusEmail" }
  | { readonly kind: "readSession" };

function isUnlocked(model: AccountPanelModel): boolean {
  return isJust(model.session) || isJust(model.localLicense);
}

export function updateAccountPanel(
  model: AccountPanelModel,
  msg: AccountPanelMsg,
): readonly [AccountPanelModel, readonly AccountPanelCmd[]] {
  switch (msg.type) {
    case "Mounted": {
      const cmds: AccountPanelCmd[] = [{ kind: "refreshLocalLicense" }];
      if (isJust(model.session)) {
        cmds.push({ kind: "refreshEntitlements" });
      }
      cmds.push({ kind: "readSession" });
      if (isUnlocked(model) === false) {
        cmds.push({ kind: "focusEmail" });
      }
      return [model, cmds];
    }
    case "EmailChanged":
      return [{ ...model, email: msg.email }, []];
    case "SendLink": {
      const trimmed = model.email.trim();
      if (trimmed.includes("@") === false) {
        return [
          {
            ...model,
            statusTone: "error",
            status: just("Enter a valid email address."),
          },
          [],
        ];
      }
      return [
        {
          ...model,
          busy: true,
          statusTone: "waiting",
          status: just("Check your email to finish signing in."),
        },
        [{ kind: "requestMagicLink", email: trimmed }],
      ];
    }
    case "ActivateFromFile":
      return [
        {
          ...model,
          busy: true,
          statusTone: "waiting",
          status: just("Opening license file…"),
        },
        [{ kind: "activateLicenseFile" }],
      ];
    case "SignOut": {
      const doneMessage = isNothing(model.session)
        ? "This device is deactivated."
        : "Signed out.";
      return [model, [{ kind: "clearSessionAndLicense", doneMessage }]];
    }
    case "Close":
      return [model, [{ kind: "emitClose" }]];
    case "CloudAuthEvent":
    case "Visibility":
      return [model, [{ kind: "readSession" }]];
    case "EntitlementsLoaded":
      return [{ ...model, entitlements: msg.entitlements }, []];
    case "EntitlementsFailed":
      return [
        {
          ...model,
          statusTone: "error",
          status: just(msg.error),
        },
        [],
      ];
    case "LicenseLoaded":
      return [{ ...model, localLicense: msg.license }, []];
    case "MagicLinkSucceeded":
      return [
        {
          ...model,
          session: just(msg.session),
          email: msg.session.email,
          busy: false,
          statusTone: "success",
          status: nothing,
        },
        [{ kind: "emitAuthChanged" }, { kind: "refreshEntitlements" }],
      ];
    case "MagicLinkFailed":
      return [
        {
          ...model,
          busy: false,
          statusTone: "error",
          status: just(msg.error),
        },
        [],
      ];
    case "ActivateSucceeded":
      return [
        {
          ...model,
          localLicense: just(msg.license),
          busy: false,
          statusTone: "success",
          status: nothing,
        },
        [{ kind: "emitAuthChanged" }],
      ];
    case "ActivateFailed":
      return [
        {
          ...model,
          busy: false,
          statusTone: "error",
          status: just(msg.error),
        },
        [],
      ];
    case "SignedOut":
      return [
        {
          ...model,
          session: nothing,
          entitlements: [],
          localLicense: nothing,
          statusTone: "idle",
          status: just(msg.doneMessage),
          busy: false,
        },
        [{ kind: "emitAuthChanged" }, { kind: "focusEmail" }],
      ];
    case "SessionSynced": {
      const wasSignedOut = isNothing(model.session);
      const next: AccountPanelModel = {
        ...model,
        session: just(msg.session),
        email: msg.session.email,
        ...(wasSignedOut === true
          ? { statusTone: "success" as const, status: nothing }
          : {}),
      };
      const cmds: AccountPanelCmd[] = [{ kind: "refreshEntitlements" }];
      if (wasSignedOut === true) {
        cmds.unshift({ kind: "emitAuthChanged" });
      }
      return [next, cmds];
    }
    default:
      return assertNever(msg);
  }
}

export const updateAccountPanelFn: UpdateFn<
  AccountPanelModel,
  AccountPanelMsg,
  AccountPanelCmd
> = updateAccountPanel;
